/**
 * The phone tab bar: fixed to the bottom of the screen below 768px, where a thumb
 * already is. Mounted by SideNav.js, so every page with the drawer has it, and
 * its Menu tab opens that same drawer. On wider screens it is not shown at all.
 *
 * The tabs come from tabs.js; the Inbox badge follows the drawer's own unread
 * count (the `pa:unread` event SideNav dispatches), so the two never disagree.
 */
import { tabsFor, activeTabId } from './tabs.js';
import { tabIconSvg } from './icons.js';

const PHONE = '(max-width: 767px)';
let bar = null;
let lastUnread = 0;

function render(user, { canEnterRealm = false } = {}) {
  const tabs = tabsFor({ user, canEnterRealm });
  const active = activeTabId(tabs, location.pathname);
  bar.innerHTML = '';
  for (const t of tabs) {
    const item = document.createElement(t.menu ? 'button' : 'a');
    item.className = `pa-tab${t.id === active ? ' is-active' : ''}`;
    item.dataset.tab = t.id;
    if (t.menu) {
      item.type = 'button';
      item.setAttribute('aria-haspopup', 'true');
      item.addEventListener('click', () => window.perAnkhSideNav?.open?.());
    } else {
      item.href = t.href;
      if (t.id === active) item.setAttribute('aria-current', 'page');
    }
    const icon = document.createElement('span');
    icon.className = 'pa-tab-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = tabIconSvg(t.icon);   // fixed drawings from icons.js
    const label = document.createElement('span');
    label.className = 'pa-tab-label';
    label.textContent = t.label;
    item.append(icon, label);
    if (t.badge) {
      const badge = document.createElement('span');
      badge.className = 'pa-tab-badge';
      badge.hidden = true;
      item.append(badge);
    }
    bar.append(item);
  }
  showUnread(lastUnread);
}

function showUnread(count) {
  lastUnread = count;
  const badge = bar?.querySelector('.pa-tab-badge');
  if (!badge) return;
  badge.hidden = !(count > 0);
  badge.textContent = count > 99 ? '99+' : String(count);
  const inbox = badge.closest('.pa-tab');
  inbox?.setAttribute('aria-label', count > 0 ? `Inbox, ${count} unread` : 'Inbox');
}

/** Build the bar for a guest; `update(user, opts)` re-renders once the session is known. */
export function mountTabBar() {
  if (bar || document.querySelector('.pa-tab-bar')) return { update() {} };
  bar = document.createElement('nav');
  bar.className = 'pa-tab-bar';
  bar.setAttribute('aria-label', 'Main');
  render(null);
  document.body.append(bar);

  const phone = window.matchMedia?.(PHONE);
  const apply = () => document.body.classList.toggle('has-tab-bar', Boolean(phone?.matches));
  apply();
  phone?.addEventListener?.('change', apply);
  window.addEventListener('pa:unread', (e) => showUnread(Number(e.detail?.count) || 0));

  return { update: (user, opts) => render(user, opts) };
}
